import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AssigneePicker } from './AssigneePicker'
import { SubjectPicker } from './SubjectPicker'
import {
  TASK_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
  type AssigneeOption,
  type TaskPriority,
  type TaskSubject,
  type TaskType,
} from '@/types/work'

export interface TaskFormState {
  title: string
  description: string
  priority: TaskPriority
  task_type: TaskType
  due_at: string
  assignee_user_id: number | null
  subject: TaskSubject | null
}

interface TaskFormProps {
  state: TaskFormState
  onChange: <K extends keyof TaskFormState>(key: K, value: TaskFormState[K]) => void
  onSubmit: (e: React.FormEvent) => void
  errors: Partial<Record<string, string>>
  processing: boolean
  assigneeOptions: AssigneeOption[]
  // Submit / cancel slot — pages decide what buttons render.
  actions: ReactNode
  // Edit form sets this true: UpdateTask::MUTABLE_KEYS does not
  // include assignee_user_id (reassignment goes through the separate
  // ReassignTask interaction with hierarchy enforcement). Showing an
  // editable picker here would silently drop the change. Render the
  // current assignee read-only with an explanatory note instead.
  // (Codex P1, PR #85)
  assigneeReadOnly?: boolean
}

// Shared form body reused by /work/tasks/new and /work/tasks/:id/edit.
// Keeps each page thin (Inertia useForm + onSuccess wiring) while the
// fields + validation rendering live in one place.
export function TaskForm({
  state,
  onChange,
  onSubmit,
  errors,
  processing,
  assigneeOptions,
  actions,
  assigneeReadOnly = false,
}: TaskFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <Field label="Titre" error={errors['title']}>
        <Input
          value={state.title}
          onChange={(e) => onChange('title', e.target.value)}
          placeholder="Titre de la tâche"
          autoFocus
        />
      </Field>

      <Field label="Description" error={errors['description']}>
        <Textarea
          value={state.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="Détails (optionnel)"
          rows={4}
        />
      </Field>

      <div className="grid gap-5 md:grid-cols-2">
        <Field label="Priorité" error={errors['priority']}>
          <Select
            value={state.priority}
            onValueChange={(v) => onChange('priority', v as TaskPriority)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(TASK_PRIORITY_LABELS) as [TaskPriority, string][]).map(
                ([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Type" error={errors['task_type']}>
          <Select
            value={state.task_type}
            onValueChange={(v) => onChange('task_type', v as TaskType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(TASK_TYPE_LABELS) as [TaskType, string][]).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Échéance" error={errors['due_at']}>
          <Input
            type="datetime-local"
            value={state.due_at}
            onChange={(e) => onChange('due_at', e.target.value)}
          />
        </Field>

        {/*
          CreateTask publishes assignment errors under multiple keys:
            - assignee_user_id  → "references a non-existent user"
            - assignee_user     → "is discarded and cannot be assigned to"
                                  "cannot be assigned to this user — insufficient hierarchy rights"
          Surface both so a submit that fails the assign? policy gives
          inline feedback instead of appearing inert. (Codex P2, PR #85)
        */}
        <Field label="Assigné à" error={errors['assignee_user_id'] || errors['assignee_user']}>
          {assigneeReadOnly ? (
            <ReadOnlyAssignee
              userId={state.assignee_user_id}
              options={assigneeOptions}
            />
          ) : (
            <AssigneePicker
              value={state.assignee_user_id}
              options={assigneeOptions}
              onChange={(id) => onChange('assignee_user_id', id)}
            />
          )}
        </Field>
      </div>

      <Field label="Lié à" error={errors['subject_id']}>
        <SubjectPicker
          value={state.subject}
          onChange={(subject) => onChange('subject', subject)}
        />
      </Field>

      <div className="flex justify-end gap-2">{actions}</div>

      {processing && <p className="text-xs text-muted-foreground">Enregistrement…</p>}
    </form>
  )
}

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

function ReadOnlyAssignee({
  userId,
  options,
}: {
  userId: number | null
  options: AssigneeOption[]
}) {
  const label = userId ? options.find((o) => o.id === userId)?.label ?? '—' : 'Non assigné'
  return (
    <div className="space-y-1">
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">{label}</div>
      <p className="text-xs text-muted-foreground">
        La réassignation se fait via le flux dédié (à venir) — ce champ n’est pas modifiable depuis l’écran de modification.
      </p>
    </div>
  )
}
